import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { kycApi } from "@/api/kyc.api";
import { queryKeys } from "@/utils/queryKeys";
import type { ListQuery } from "@/types/common";
import type { KycStatus } from "@/types/domain";

export function useKycList(query: ListQuery & { status?: KycStatus }) {
  return useQuery({
    queryKey: queryKeys.kyc.list(query),
    queryFn: () => kycApi.list(query),
    placeholderData: (prev) => prev,
  });
}

export function useKycApplication(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.kyc.detail(id ?? ""),
    queryFn: () => kycApi.getById(id as string),
    enabled: !!id,
  });
}

export function useApproveKyc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) => kycApi.approve(id, notes),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kyc.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["kyc", "list"] });
    },
  });
}

export function useRejectKyc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => kycApi.reject(id, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kyc.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["kyc", "list"] });
    },
  });
}

/** Fetches a fresh short-lived S3 view URL and opens it in a new tab. */
export function useOpenKycDocument() {
  return useMutation({
    mutationFn: (documentId: string) => kycApi.getDocumentUrl(documentId),
    onSuccess: (data) => {
      window.open(data.url, "_blank", "noopener,noreferrer");
    },
  });
}
